// ===== Production Performance Monitor =====

import React from 'react';
import { PERFORMANCE_THRESHOLDS } from '../constants/ChatConstants';

export class PerformanceMonitor {
  constructor(context = 'ChatApp') {
    this.context = context;
    this.metrics = {
      renderTimes: [],
      memoryUsage: [],
      networkLatency: [],
      callSetupTimes: [],
      messageDeliveryTimes: []
    };
    this.isMonitoring = false;
    this.intervalId = null;
    this.observers = [];
    
    // Performance API support check
    this.supportsPerformanceAPI = typeof performance !== 'undefined' && 
                                  typeof performance.mark === 'function';
    
    // Memory API support check
    this.supportsMemoryAPI = typeof performance.memory !== 'undefined';
    
    this.init();
  }

  init() {
    // Set up Performance Observer if available
    if (typeof PerformanceObserver !== 'undefined') {
      try {
        // Monitor navigation timing
        const navObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach(entry => {
            this.recordMetric('navigation', entry.duration);
          });
        });
        navObserver.observe({ entryTypes: ['navigation'] });
        this.observers.push(navObserver);

        // Monitor resource timing
        const resourceObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach(entry => {
            if (entry.name.includes('socket.io') || entry.name.includes('api')) {
              this.recordNetworkLatency(entry.duration);
            }
          });
        });
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.push(resourceObserver);

        // Monitor measure timing
        const measureObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach(entry => {
            this.recordMetric(entry.name, entry.duration);
          });
        });
        measureObserver.observe({ entryTypes: ['measure'] });
        this.observers.push(measureObserver);

      } catch (error) {
        console.warn('Performance Observer not fully supported:', error);
      }
    }
  }

  /**
   * Start monitoring
   */
  start() {
    if (this.isMonitoring) return;
    
    this.isMonitoring = true;
    console.log(`[${this.context}] Performance monitoring started`);
    
    // Monitor memory usage every 30 seconds
    this.intervalId = setInterval(() => {
      this.checkMemoryUsage();
      this.checkPerformanceThresholds();
    }, 30000);
  }

  /**
   * Stop monitoring
   */
  stop() {
    if (!this.isMonitoring) return;
    
    this.isMonitoring = false;
    
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    
    // Disconnect observers
    this.observers.forEach(observer => {
      try {
        observer.disconnect();
      } catch (error) {
        console.warn('Error disconnecting observer:', error);
      }
    });
    this.observers = [];
    
    console.log(`[${this.context}] Performance monitoring stopped`);
  }

  /**
   * Record a performance metric
   */
  recordMetric(name, value, metadata = {}) {
    const metric = {
      name,
      value,
      timestamp: Date.now(),
      metadata
    };

    // Store in appropriate category
    switch (name) {
      case 'render':
      case 'component-render':
        this.metrics.renderTimes.push(metric);
        break;
      case 'call-setup':
        this.metrics.callSetupTimes.push(metric);
        break;
      case 'message-delivery':
        this.metrics.messageDeliveryTimes.push(metric);
        break;
      default:
        if (!this.metrics[name]) {
          this.metrics[name] = [];
        }
        this.metrics[name].push(metric);
    }

    // Keep only recent metrics (last 100)
    Object.keys(this.metrics).forEach(key => {
      if (Array.isArray(this.metrics[key]) && this.metrics[key].length > 100) {
        this.metrics[key] = this.metrics[key].slice(-100);
      }
    });

    // Log warnings for slow operations
    this.checkThreshold(name, value);
  }

  /**
   * Check if metric exceeds threshold
   */
  checkThreshold(name, value) {
    const thresholds = {
      'render': PERFORMANCE_THRESHOLDS.MESSAGE_RENDER_TIME,
      'component-render': PERFORMANCE_THRESHOLDS.MESSAGE_RENDER_TIME,
      'call-setup': PERFORMANCE_THRESHOLDS.CALL_SETUP_TIME,
      'connection': PERFORMANCE_THRESHOLDS.CONNECTION_TIMEOUT
    };

    const threshold = thresholds[name];
    if (threshold && value > threshold) {
      console.warn(`[${this.context}] Performance warning: ${name} took ${value}ms (threshold: ${threshold}ms)`);
    }
  }

  /**
   * Start performance mark
   */
  startMark(name) {
    if (!this.supportsPerformanceAPI) return;
    
    try {
      performance.mark(`${name}-start`);
    } catch (error) {
      console.warn('Error creating performance mark:', error);
    }
  }

  /**
   * End performance mark and measure
   */
  endMark(name) {
    if (!this.supportsPerformanceAPI) return;
    
    try {
      performance.mark(`${name}-end`);
      performance.measure(name, `${name}-start`, `${name}-end`);
      
      // Get the measurement
      const measures = performance.getEntriesByName(name, 'measure');
      if (measures.length > 0) {
        const duration = measures[measures.length - 1].duration;
        this.recordMetric(name, duration);
        
        // Clean up marks
        performance.clearMarks(`${name}-start`);
        performance.clearMarks(`${name}-end`);
        performance.clearMeasures(name);
        
        return duration;
      }
    } catch (error) {
      console.warn('Error measuring performance:', error);
    }
    
    return 0;
  }

  /**
   * Record network latency
   */
  recordNetworkLatency(latency) {
    this.metrics.networkLatency.push({
      value: latency,
      timestamp: Date.now()
    });
  }

  /**
   * Check memory usage
   */
  checkMemoryUsage() {
    if (!this.supportsMemoryAPI) return;
    
    try {
      const memory = performance.memory;
      const usage = {
        used: memory.usedJSHeapSize,
        total: memory.totalJSHeapSize,
        limit: memory.jsHeapSizeLimit,
        timestamp: Date.now()
      };
      
      this.metrics.memoryUsage.push(usage);
      
      // Check memory threshold
      if (usage.used > PERFORMANCE_THRESHOLDS.MEMORY_WARNING) {
        console.warn(`[${this.context}] Memory warning: ${(usage.used / (1024 * 1024)).toFixed(2)}MB used`);
      }
      
      // Keep only recent memory readings
      if (this.metrics.memoryUsage.length > 50) {
        this.metrics.memoryUsage = this.metrics.memoryUsage.slice(-50);
      }
      
    } catch (error) {
      console.warn('Error checking memory usage:', error);
    }
  }

  /**
   * Check various performance thresholds
   */
  checkPerformanceThresholds() {
    // Check render times
    const recentRenders = this.metrics.renderTimes.slice(-10);
    if (recentRenders.length > 0) {
      const avgRenderTime = recentRenders.reduce((sum, m) => sum + m.value, 0) / recentRenders.length;
      if (avgRenderTime > PERFORMANCE_THRESHOLDS.MESSAGE_RENDER_TIME) {
        console.warn(`[${this.context}] Average render time high: ${avgRenderTime.toFixed(2)}ms`);
      }
    }

    // Check call setup times
    const recentCalls = this.metrics.callSetupTimes.slice(-5);
    if (recentCalls.length > 0) {
      const avgCallSetup = recentCalls.reduce((sum, m) => sum + m.value, 0) / recentCalls.length;
      if (avgCallSetup > PERFORMANCE_THRESHOLDS.CALL_SETUP_TIME) {
        console.warn(`[${this.context}] Average call setup time high: ${avgCallSetup.toFixed(2)}ms`);
      }
    }
  }

  /**
   * Get performance summary
   */
  getSummary() {
    const summary = {
      context: this.context,
      isMonitoring: this.isMonitoring,
      timestamp: new Date().toISOString(),
      metrics: {}
    };

    // Calculate averages for each metric type
    Object.keys(this.metrics).forEach(key => {
      const values = this.metrics[key];
      if (Array.isArray(values) && values.length > 0) {
        if (key === 'memoryUsage') {
          const latest = values[values.length - 1];
          summary.metrics[key] = {
            current: latest,
            count: values.length
          };
        } else {
          const numericValues = values.map(v => typeof v === 'object' ? v.value : v);
          summary.metrics[key] = {
            count: values.length,
            average: numericValues.reduce((sum, val) => sum + val, 0) / numericValues.length,
            min: Math.min(...numericValues),
            max: Math.max(...numericValues),
            latest: numericValues[numericValues.length - 1]
          };
        }
      }
    });

    return summary;
  }

  /**
   * Export performance data
   */
  exportData() {
    const data = {
      context: this.context,
      exportTime: new Date().toISOString(),
      summary: this.getSummary(),
      rawMetrics: this.metrics
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { 
      type: 'application/json' 
    });
    
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-${this.context}-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    Object.keys(this.metrics).forEach(key => {
      this.metrics[key] = [];
    });
    console.log(`[${this.context}] Performance metrics cleared`);
  }

  /**
   * Get real-time stats
   */
  getRealTimeStats() {
    const stats = {
      timestamp: Date.now(),
      memory: null,
      navigation: null
    };

    // Memory info
    if (this.supportsMemoryAPI && performance.memory) {
      stats.memory = {
        used: Math.round(performance.memory.usedJSHeapSize / (1024 * 1024)),
        total: Math.round(performance.memory.totalJSHeapSize / (1024 * 1024)),
        limit: Math.round(performance.memory.jsHeapSizeLimit / (1024 * 1024))
      };
    }

    // Navigation timing
    if (performance.timing) {
      const timing = performance.timing;
      stats.navigation = {
        loadTime: timing.loadEventEnd - timing.navigationStart,
        domReady: timing.domContentLoadedEventEnd - timing.navigationStart,
        firstByte: timing.responseStart - timing.navigationStart
      };
    }

    return stats;
  }
}

/**
 * HOC for monitoring component render performance
 */
export const withPerformanceMonitoring = (WrappedComponent, componentName) => {
  return React.forwardRef((props, ref) => {
    const monitor = React.useRef(new PerformanceMonitor(componentName));
    
    React.useEffect(() => {
      monitor.current.start();
      return () => monitor.current.stop();
    }, []);

    React.useEffect(() => {
      monitor.current.startMark('component-render');
      return () => {
        monitor.current.endMark('component-render');
      };
    });

    return React.createElement(WrappedComponent, { ...props, ref });
  });
};

export default PerformanceMonitor;
